import {
  EllipsisOutlined,
  ExclamationOutlined,
  LaptopOutlined,
} from '@ant-design/icons';
import {
  Form,
  Button,
  List,
  Row,
  Input,
  Select,
  Dropdown,
  Menu,
  Modal,
} from 'antd';
import LangSwitch from 'app/components/LangSwitch/LangSwitch';
import { Link } from 'app/components/Link';
import { RouteConstants } from 'app/routes';
import React, { ReactElement, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { TextRegular14 } from 'styles/typography';
import { localStorageEnum } from 'types';
import { useHiikoEnvSlice } from '../../slice';
import { selectEnvs, selectIsCreateEnvLoading } from '../../slice/selectors';
import { createEnvFormEnum, ICreateEnvPayload, IEnv } from '../../slice/types';

import * as S from './HiikoEnvList.styles';

interface IProps {}
const CREATE_ENV_FORM_ID = 'createEnvForm';

const HiikoEnvList = (props: IProps): ReactElement => {
  const { t } = useTranslation();

  const history = useHistory();
  const location = useLocation();

  const isCreateEnvFormVisible =
    location.pathname === RouteConstants.hiikoEnv.createEnv;

  const dispatch = useDispatch();

  const [form] = Form.useForm();
  const { actions } = useHiikoEnvSlice();

  const loading = useSelector(selectIsCreateEnvLoading);

  const envs = useSelector(selectEnvs);

  const onCreateEnv = (values: ICreateEnvPayload) => {
    dispatch(actions.createEnv(values));
  };

  const { primary } = useTheme();

  useEffect(() => {
    if (!isCreateEnvFormVisible) {
      form.resetFields();
    }
  }, [isCreateEnvFormVisible, form]);

  const onArchiveClick = (envId: number) => {
    Modal.confirm({
      style: { width: 425 },
      title: t(
        'HiikoEnv.CreateEnv.AreYouSureYouWantArchiveEnv',
        'Are you sure you want to archive the environment?',
      ),
      icon: <ExclamationOutlined />,
      content: t(
        'HiikoEnv.CreateEnv.ContactAdminToRestoreEnv',
        'If you will want to restore the environment you will need to contact the administrator',
      ),
      okText: t('HiikoEnv.CreateEnv.Confirm', 'Confirm'),
      cancelText: t('HiikoEnv.CreateEnv.Cancel', 'Cancel'),
      onOk() {
        dispatch(actions.archiveEnv(envId));
      },
    });
  };

  const onLogout = () => {
    localStorage.removeItem(localStorageEnum.token);
    localStorage.removeItem(localStorageEnum.envId);

    // easiest way to clean all local state - reload window
    window.location.reload();
  };

  return (
    <>
      <S.LayoutWrapper>
        <Row justify="end">
          <LangSwitch />
          <Button type="text" onClick={onLogout}>
            {t('Header.Logout', 'Logout')}
          </Button>
        </Row>
        <S.LogoRow justify="center">
          <S.Logo />
          <S.LogoText />
        </S.LogoRow>
        <S.MainRow justify="center">
          <S.EnvsCard
            title={
              isCreateEnvFormVisible
                ? t(
                    'HiikoEnv.CreateEnv.CreateNewEnv',
                    'Create a new environment',
                  )
                : t('HiikoEnv.CreateEnv.ChooseEnv', 'Choose an environment')
            }
            bordered={false}
            headStyle={{ border: 'none', textAlign: 'center' }}
          >
            {isCreateEnvFormVisible ? (
              <Form
                form={form}
                id={CREATE_ENV_FORM_ID}
                onFinish={onCreateEnv}
                initialValues={{ [createEnvFormEnum.countryCode]: 'lt' }}
              >
                <Form.Item
                  name={createEnvFormEnum.envName}
                  rules={[{ required: true }]}
                >
                  <Input
                    placeholder={t(
                      'HiikoEnv.CreateEnv.EnterName',
                      'Enter a name',
                    )}
                    size="large"
                    prefix={<LaptopOutlined style={{ color: primary }} />}
                  />
                </Form.Item>
                <Form.Item
                  name={createEnvFormEnum.countryCode}
                  rules={[{ required: true }]}
                >
                  <Select
                    size="large"
                    placeholder={t('HiikoEnv.CreateEnv.Country', 'Country')}
                    getPopupContainer={trigger => trigger.parentNode}
                  >
                    <Select.Option key="lt" value="lt">
                      Lietuva
                    </Select.Option>
                  </Select>
                </Form.Item>
                <Button
                  loading={loading}
                  form={CREATE_ENV_FORM_ID}
                  key="submit"
                  htmlType="submit"
                  type="primary"
                  block
                  size="large"
                >
                  {t('HiikoEnv.CreateEnv.Create', 'Create')}
                </Button>
              </Form>
            ) : (
              <List
                itemLayout="horizontal"
                dataSource={envs}
                renderItem={(env: IEnv) => (
                  <List.Item
                    actions={[
                      <Link
                        to={generatePath(RouteConstants.company.list, {
                          envId: env.id,
                        })}
                      >
                        {t('HiikoEnv.CreateEnv.Connect', 'Connect')}
                      </Link>,
                      <Dropdown
                        trigger={['click']}
                        disabled={env.role !== 'OWNER'}
                        overlay={
                          <Menu>
                            <Menu.Item onClick={() => onArchiveClick(env.id)}>
                              {t('Common.Archive', 'Archive')}
                            </Menu.Item>
                          </Menu>
                        }
                      >
                        <EllipsisOutlined
                          style={{
                            opacity: env.role !== 'OWNER' ? 0.3 : 1,
                          }}
                        />
                      </Dropdown>,
                    ]}
                  >
                    {env.name}
                  </List.Item>
                )}
              />
            )}
            <Row justify="center" align="middle" style={{ marginTop: '14px' }}>
              {isCreateEnvFormVisible ? (
                <>
                  <TextRegular14>
                    {t(
                      'HiikoEnv.CreateEnv.HaveYouChangedYourMind',
                      'Have you changed your mind?',
                    )}
                  </TextRegular14>
                  <Button
                    type="link"
                    onClick={() => history.push(RouteConstants.hiikoEnv.list)}
                  >
                    {t('HiikoEnv.CreateEnv.Back', 'Back')}
                  </Button>
                </>
              ) : (
                <>
                  {/* <Button
                 type="link"
                 onClick={() =>
                      () => history.push(RouteConstants.hiikoEnv.createEnv)

                 }
               >
                 {t('HiikoEnv.CreateEnv.CreateNew', 'Create a new')}
               </Button> */}
                </>
              )}
            </Row>
          </S.EnvsCard>
        </S.MainRow>

        <Row justify="center">
          <TextRegular14>
            {t('Auth.FooterText', 'www.hiiko.eu | www.horiondigital.com')}
          </TextRegular14>
        </Row>
      </S.LayoutWrapper>
    </>
  );
};

export default HiikoEnvList;
